import EmailDirectoryAdmin from "../components/EmailDirectoryAdmin";
import AdminNotificationCount from "../components/AdminNotificationCount";
import AdminNotifications from "../components/AdminNotifications";
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Eye,
  EyeOff,
  Shield,
  Info,
  Plus,
  Search,
  ArrowLeft,
  Edit,
  RotateCw,
  FileText,
  Upload,
  Trash2,
  Package,
  ShoppingBag,
  RefreshCw,
  Music as MusicIcon,
  ExternalLink,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import DailyScriptureAdmin from "../components/DailyScriptureAdmin";
import { toast } from "sonner";
import axios from "axios";
import {
  ALL_PERMISSIONS,
  ALL_ADMIN_SCOPES,
  EXTENDED_ADMIN_SCOPES,
  MINISTRY_DEFINITIONS,
  getMinistryLabel,
  normalizeMinistryKeys,
} from "../constants/permissions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getCookie(name) {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? decodeURIComponent(match[2]) : null;
}

function getAuthHeader() {
  const csrf =
    getCookie("ysq_csrf") ||
    localStorage.getItem("ysq_csrf");

  return {
    withCredentials: true,
    headers: csrf ? { "X-CSRF-Token": csrf } : {},
  };
}

async function ensureCsrfToken() {
  const response = await axios.get(`${API}/auth/csrf`, {
    withCredentials: true,
  });

  const token =
    getCookie("ysq_csrf") ||
    response.data?.csrf_token;

  if (token) {
    localStorage.setItem("ysq_csrf", token);
  }

  return token;
}

const ADMIN_SCOPE_LABELS = {
  "*": "Protected Super Admin",
  all_except_super_admin: "Mini Super Admin",
  user_management: "User Management",
  portal_operations: "Website / Portal",
  subscriber_assets: "Subscriber Assets",
  commerce_products: "Store Products",
  payments_claims: "Payments and Claims",
  subscriber_accounts: "Subscriptions",
  submissions_review: "Submissions",
  website_content: "Website Content",
  media_library: "Media Library",
};

const ADMIN_SCOPE_DESCRIPTIONS = {
  "*": "Full protected access reserved for the protected super admin.",
  all_except_super_admin:
    "Broad admin access, but cannot change the protected super admin account.",
  user_management:
    "Can manage user accounts, roles, and user records according to assigned limits.",
  portal_operations:
    "Can manage portal-facing website content and portal administration areas.",
  subscriber_assets:
    "Can manage subscriber files, downloads, gated resources, and media assets.",
  commerce_products:
    "Can manage store products, merch, music, media products, and product records.",
  subscriber_accounts:
    "Can manage subscriptions, subscription status, orders, and payment records.",
  submissions_review:
    "Can review and manage applications, leads, contact messages, and submission records.",
  website_content:
    "Can view or assist with website content records.",
  payments_claims:
  "Can review payment-related records, patron claims, order issues, and claim follow-up.",
  media_library:
    "Can view or assist with media and asset records.",
};

function getAdminScopeLabel(scope) {
  if (ADMIN_SCOPE_LABELS[scope]) return ADMIN_SCOPE_LABELS[scope];

  const ministryLabel = getMinistryLabel(scope);
  if (ministryLabel && ministryLabel !== scope) return ministryLabel;

  const found = ALL_ADMIN_SCOPES.find((s) => s.key === scope);
  return found?.label || scope;
}

function getAdminScopeDescription(scope) {
  if (ADMIN_SCOPE_DESCRIPTIONS[scope]) return ADMIN_SCOPE_DESCRIPTIONS[scope];

  const found = ALL_ADMIN_SCOPES.find((s) => s.key === scope);
  return found?.description || "";
}

function getAdminLevel(u) {
  const scopes = u.admin_scopes || [];
  const roles = u.roles || [];

  if (u.username === "sharahshaba" || scopes.includes("*")) {
    return "Protected Super Admin";
  }

  if (scopes.includes("all_except_super_admin")) {
    return "Mini Super Admin";
  }

  if (roles.includes("admin") && scopes.length > 0) {
    return "Scoped Admin";
  }

  if (roles.includes("admin")) {
    return "Admin";
  }

  return "Standard User";
}

export default function NationalsAdmin() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterRole, setFilterRole] = useState("all");
  const [activeTab, setActiveTab] = useState("users");
  const [assetMonth, setAssetMonth] = useState(new Date().toISOString().slice(0, 7));

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [showAssetModal, setShowAssetModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [resetPassword, setResetPassword] = useState("");
  const [showResetPassword, setShowResetPassword] = useState(false);

  const [formData, setFormData] = useState({
    email: "",
    username: "",
    display_name: "",
    real_name: "",
    password: "",
    ministries: [],
    service_programs: {},
    roles: ["national"],
    permissions: [],
    admin_scopes: [],
    status: "active",
    subscription_tier: "",
    subscription_status: "",
  });

  const [assetForm, setAssetForm] = useState({
    title: "",
    month_key: "",
    asset_type: "download",
    file_url: "",
    thumbnail_url: "",
    access_tiers: ["supporter", "builder", "patron"],
    is_active: true,
  });

  const [editingAsset, setEditingAsset] = useState(null);

  const [products, setProducts] = useState([]);
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productSyncing, setProductSyncing] = useState(false);

  const [productForm, setProductForm] = useState({
    id: "",
    title: "",
    category: "music",
    product_type: "music",
    price: 0.99,
    description: "",
    artist: "",
    cover_image_url: "",
    audio_file_url: "",
    preview_file_url: "",
    preview_duration_seconds: 20,
    external_url: "",
    source: "internal",
    digital: true,
    is_active: true,
  });

  const blankUserForm = {
    email: "",
    username: "",
    display_name: "",
    real_name: "",
    password: "",
    ministries: [],
    roles: ["national"],
    permissions: [],
    admin_scopes: [],
    status: "active",
    subscription_tier: "",
    subscription_status: "",
  };

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/nationals/admin/users`, getAuthHeader());
      setUsers(response.data);
    } catch (error) {
      toast.error("Failed to fetch users");
    }
  }, []);

  const fetchAssets = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API}/admin/subscriber-assets?month=${assetMonth}`,
        getAuthHeader()
      );
      setAssets(response.data);
    } catch (error) {
      toast.error("Failed to fetch assets");
    }
  }, [assetMonth]);

  const fetchProducts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/products`, getAuthHeader());
      setProducts(response.data);
    } catch (error) {
      toast.error("Failed to fetch products");
    }
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/nationals/me`, getAuthHeader());
        const respData = response.data;
        const roles = respData.roles || [];

        if (!roles.includes("admin")) {
          toast.error("Admin access required");
          navigate("/portal");
          return;
        }

        setUser(respData);
        setLoading(false);
      } catch {
        navigate("/nationals/login");
      }
    };

    checkAuth();
  }, [navigate]);

  useEffect(() => {
    if (user) fetchUsers();
  }, [user, fetchUsers]);

  useEffect(() => {
    if (user && activeTab === "assets") fetchAssets();
  }, [user, activeTab, fetchAssets]);

  useEffect(() => {
    if (user && activeTab === "products") fetchProducts();
  }, [user, activeTab, fetchProducts]);

  const userAdminScopes = user?.admin_scopes || [];

  const hasManagedMinistryScope = userAdminScopes.some((scope) =>
    MINISTRY_DEFINITIONS.some((ministry) => ministry.key === scope)
  );

  const canManageUsers =
    user &&
    (
      user.username === "sharahshaba" ||
      userAdminScopes.includes("*") ||
      userAdminScopes.includes("user_management") ||
      userAdminScopes.includes("all_except_super_admin") ||
      hasManagedMinistryScope ||
      (user.permissions || []).includes("users:manage")
    );

  const canManageAssets =
    user &&
    ("*" === (user.admin_scopes || [])[0] ||
      (user.permissions || []).includes("assets:manage"));

  const canManageProducts =
    user &&
    ("*" === (user.admin_scopes || [])[0] ||
      (user.permissions || []).includes("products:manage") ||
      (user.permissions || []).includes("assets:manage"));

  const [diagnosticUnread, setDiagnosticUnread] = useState(0);
  useEffect(() => {
    if (!user || !((user.admin_scopes || []).includes("*") || (user.permissions || []).includes("portal:read"))) return undefined;
    let active = true;
    const update = () => axios.get(`${API}/diagnostics/admin/reports`, { params: { status: "new", limit: 300 } })
      .then(({ data }) => { if (active) setDiagnosticUnread(data.total || 0); })
      .catch(() => {});
    update();
    const timer = window.setInterval(update, 60000);
    return () => { active = false; window.clearInterval(timer); };
  }, [user]);

  const canViewDiagnostics =
    user &&
    ((user.admin_scopes || []).includes("*") ||
      (user.permissions || []).includes("portal:read"));

  const canViewDailyScripture =
    user && (user.roles || []).includes("admin");

  const getApiErrorMessage = (error, fallback = "Request failed") => {
    const detail = error?.response?.data?.detail;

    if (Array.isArray(detail)) {
      return detail
        .map((item) => item?.msg || item?.message || JSON.stringify(item))
        .join(", ");
    }

    if (typeof detail === "string") {
      return detail;
    }

    if (detail && typeof detail === "object") {
      return detail.message || JSON.stringify(detail);
    }

    return error?.message || fallback;
  };

  const handleCreateUser = async () => {
    try {
      await ensureCsrfToken();

      await axios.post(
        `${API}/nationals/admin/users`,
        {
          ...formData,
          ministries: normalizeMinistryKeys(formData.ministries || []),
          admin_scopes: formData.admin_scopes || [],
        },
        getAuthHeader()
      );

      toast.success("User created");
      setShowCreateModal(false);
      setFormData(blankUserForm);
      fetchUsers();
    } catch (error) {
      console.error("User create failed:", error?.response?.data || error);
      toast.error(getApiErrorMessage(error, "Failed to create user"));
    }
  };

  const handleUpdateUser = async () => {
    try {
      await ensureCsrfToken();

      await axios.patch(
        `${API}/nationals/admin/users/${selectedUser.id}`,
        {
          status: formData.status,
          roles: formData.roles,
          permissions: formData.permissions,
          ministries: normalizeMinistryKeys(formData.ministries || []),
          admin_scopes: formData.admin_scopes || [],
          service_programs: formData.service_programs || {},
          subscription_tier: formData.subscription_tier || null,
          subscription_status: formData.subscription_status || null,
        },
        getAuthHeader()
      );

      toast.success("User updated");
      setShowEditModal(false);
      fetchUsers();
    } catch (error) {
      console.error("User update failed:", error?.response?.data || error);
      toast.error(getApiErrorMessage(error, "Failed to update user"));
    }
  };

  const handleResetPassword = async () => {
    try {
      await ensureCsrfToken();

      await axios.post(
        `${API}/nationals/admin/users/${selectedUser.id}/reset-password`,
        { new_password: resetPassword },
        getAuthHeader()
      );

      toast.success("Password reset");
      setShowResetModal(false);
      setResetPassword("");
    } catch (error) {
      console.error("Password reset failed:", error?.response?.data || error);
      toast.error(getApiErrorMessage(error, "Failed to reset password"));
    }
  };

  const handleViewAudit = async (u) => {
    try {
      const response = await axios.get(
        `${API}/nationals/admin/audit/${u.id}`,
        getAuthHeader()
      );

      setAuditLogs(response.data);
      setSelectedUser(u);
      setShowAuditModal(true);
    } catch (error) {
      console.error("Audit log load failed:", error?.response?.data || error);
      toast.error(getApiErrorMessage(error, "Failed to load audit log"));
    }
  };

  const openEditModal = (u) => {
    setSelectedUser(u);

    setFormData({
      ...u,
      status: u.status || "active",
      roles: u.roles || ["national"],
      permissions: u.permissions || [],
      ministries: normalizeMinistryKeys(u.ministries || []),
      admin_scopes: u.admin_scopes || [],
      subscription_tier: u.subscription_tier || "",
      subscription_status: u.subscription_status || "",
    });

    setShowEditModal(true);
  };

  const handleCreateAsset = async () => {
    try {
      await ensureCsrfToken();

      await axios.post(
        `${API}/admin/subscriber-assets`,
        assetForm,
        getAuthHeader()
      );

      toast.success("Asset created");
      setShowAssetModal(false);
      setAssetForm({
        title: "",
        month_key: assetMonth,
        asset_type: "download",
        file_url: "",
        thumbnail_url: "",
        access_tiers: ["supporter", "builder", "patron"],
        is_active: true,
      });
      fetchAssets();
    } catch (error) {
      console.error("Asset create failed:", error?.response?.data || error);
      toast.error(getApiErrorMessage(error, "Failed to create asset"));
    }
  };

  const handleUpdateAsset = async () => {
    try {
      await ensureCsrfToken();

      await axios.patch(
        `${API}/admin/subscriber-assets/${editingAsset.id}`,
        assetForm,
        getAuthHeader()
      );

      toast.success("Asset updated");
      setEditingAsset(null);
      setShowAssetModal(false);
      fetchAssets();
    } catch (error) {
      console.error("Asset update failed:", error?.response?.data || error);
      toast.error(getApiErrorMessage(error, "Failed to update asset"));
    }
  };

  const handleDeleteAsset = async (id) => {
    if (!window.confirm("Delete this asset?")) return;

    try {
      await ensureCsrfToken();

      await axios.delete(
        `${API}/admin/subscriber-assets/${id}`,
        getAuthHeader()
      );

      toast.success("Asset deleted");
      fetchAssets();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const openProductCreate = () => {
    setEditingProduct(null);
    setProductForm({
      id: "",
      title: "",
      category: "music",
      product_type: "music",
      price: 0.99,
      description: "",
      artist: "",
      cover_image_url: "",
      audio_file_url: "",
      preview_file_url: "",
      preview_duration_seconds: 20,
      external_url: "",
      source: "internal",
      digital: true,
      is_active: true,
    });
    setShowProductModal(true);
  };

  const openProductEdit = (p) => {
    setEditingProduct(p);
    setProductForm({
      id: p.id || "",
      title: p.title || p.name || "",
      category: p.category || "music",
      product_type: p.product_type || "music",
      price: p.price || 0.99,
      description: p.description || "",
      artist: p.artist || "",
      cover_image_url: p.cover_image_url || "",
      audio_file_url: p.audio_file_url || "",
      preview_file_url: p.preview_file_url || "",
      preview_duration_seconds: p.preview_duration_seconds || 20,
      external_url: p.external_url || "",
      source: p.source || "internal",
      digital: p.digital !== false,
      is_active: p.is_active !== false,
    });
    setShowProductModal(true);
  };

  const handleSaveProduct = async () => {
    try {
      await ensureCsrfToken();

      if (editingProduct) {
        const { id: _id, ...patch } = productForm;

        await axios.patch(
          `${API}/admin/products/${editingProduct.id}`,
          patch,
          getAuthHeader()
        );

        toast.success("Product updated");
      } else {
        await axios.post(
          `${API}/admin/products`,
          productForm,
          getAuthHeader()
        );

        toast.success("Product created");
      }

      setShowProductModal(false);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save product");
    }
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;

    try {
      await ensureCsrfToken();

      await axios.delete(
        `${API}/admin/products/${id}`,
        getAuthHeader()
      );

      toast.success("Product deleted");
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete");
    }
  };

  const handleEtsySync = async () => {
    setProductSyncing(true);

    try {
      await ensureCsrfToken();

      const res = await axios.post(
        `${API}/admin/etsy-sync`,
        {},
        getAuthHeader()
      );

      toast.success(res.data?.message || "Etsy listings synced");
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Etsy sync failed");
    } finally {
      setProductSyncing(false);
    }
  };

  const handleSeedMusic = async () => {
    setProductSyncing(true);

    try {
      await ensureCsrfToken();

      const res = await axios.post(
        `${API}/admin/products/seed-music`,
        {},
        getAuthHeader()
      );

      toast.success(res.data?.message || "Music products seeded");
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Seed music failed");
    } finally {
      setProductSyncing(false);
    }
  };

  const applyPermissionPreset = (preset) => {
    const presetConfig = {
      mini_super_admin: {
        scopes: ["all_except_super_admin"],
        permissions: [
          "users:read",
          "users:manage",
          "roles:manage",
          "permissions:manage",
          "ministries:read",
          "ministries:manage",
          "subscriptions:read",
          "subscriptions:manage",
          "assets:read",
          "assets:manage",
          "products:read",
          "products:manage",
          "portal:read",
          "portal:manage",
          "submissions:read",
          "submissions:manage",
        ],
      },

      user_admin: {
        scopes: ["user_management"],
        permissions: ["users:read", "users:manage", "roles:manage"],
      },

      portal_admin: {
        scopes: ["portal_operations"],
        permissions: ["portal:read", "portal:manage"],
      },

      assets_admin: {
        scopes: ["subscriber_assets"],
        permissions: ["assets:read", "assets:manage"],
      },

      products_admin: {
        scopes: ["commerce_products"],
        permissions: ["products:read", "products:manage"],
      },

      subscriptions_admin: {
        scopes: ["subscriber_accounts"],
        permissions: ["subscriptions:read", "subscriptions:manage"],
      },

      submissions_admin: {
        scopes: ["submissions_review"],
        permissions: ["submissions:read", "submissions:manage"],
      },

      ministry_admin: {
        scopes: [],
        permissions: [
          "users:read",
          "users:manage",
          "roles:manage",
          "ministries:read",
          "ministries:manage",
        ],
      },
    };

    const config = presetConfig[preset];

    if (!config) return;

    setFormData((prev) => ({
      ...prev,
      permissions: Array.from(
        new Set([...(prev.permissions || []), ...(config.permissions || [])])
      ),
      admin_scopes: Array.from(
        new Set([...(prev.admin_scopes || []), ...(config.scopes || [])])
      ),
    }));
  };

  const togglePermission = (perm) => {
    setFormData((prev) => {
      const currentPermissions = prev.permissions || [];
      const currentRoles = prev.roles || [];
      const currentScopes = prev.admin_scopes || [];

      const isRemoving = currentPermissions.includes(perm);

      const nextPermissions = isRemoving
        ? currentPermissions.filter((p) => p !== perm)
        : [...currentPermissions, perm];

      const userManagementPermissions = [
        "users:read",
        "users:manage",
        "roles:manage",
      ];

      const isUserManagementPermission =
        userManagementPermissions.includes(perm);

      const shouldRemoveUserManagementScope =
        isRemoving && isUserManagementPermission;

      const nextScopes = shouldRemoveUserManagementScope
        ? currentScopes.filter((scope) => scope !== "user_management")
        : currentScopes;

      return {
        ...prev,
        roles: currentRoles.includes("admin")
          ? currentRoles
          : [...currentRoles, "admin"],
        permissions: nextPermissions,
        admin_scopes: nextScopes,
      };
    });
  };

  const toggleRole = (role) => {
    setFormData((prev) => {
      const roles = prev.roles || [];
      const isRemoving = roles.includes(role);

      const nextRoles = isRemoving
        ? roles.filter((r) => r !== role)
        : [...roles, role];

      if (role === "admin" && isRemoving) {
        return {
          ...prev,
          roles: nextRoles,
          permissions: [],
          admin_scopes: [],
        };
      }

      return {
        ...prev,
        roles: nextRoles,
      };
    });
  };

  const toggleMinistry = (m) => {
    const normalizedKey = normalizeMinistryKeys([m])[0];
    const mins = normalizeMinistryKeys(formData.ministries || []);

    setFormData({
      ...formData,
      ministries: mins.includes(normalizedKey)
        ? mins.filter((x) => x !== normalizedKey)
        : [...mins, normalizedKey],
    });
  };

  const getFrontendScopeDefaultPermissions = (scope) => {
    const scopePermissions = {
      all_except_super_admin: [],
      email_communications: ["email_contacts:read", "email_contacts:manage", "email_campaigns:read", "email_campaigns:manage"],
      records_administration: ["records:read", "records:manage"],
      publishing_administration: ["publishing:read", "publishing:manage"],
      live_administration: ["live:read", "live:manage"],
      music_subscriber_administration: ["subscriptions:read", "subscriptions:manage"],
      merch_administration: ["merch:read", "merch:manage"],
      user_management: ["users:read", "users:manage", "roles:manage"],

      portal_operations: [
        "portal:read",
        "portal:manage",
        "posts:read",
        "posts:manage",
      ],

      subscriber_assets: ["assets:read", "assets:manage"],

      commerce_products: [
        "products:read",
        "products:manage",
        "merch:read",
        "merch:manage",
        "music:read",
        "music:manage",
        "media:read",
        "media:manage",
      ],

      subscriber_accounts: [
        "subscriptions:read",
        "subscriptions:manage",
        "orders:read",
        "orders:manage",
        "payments:read",
        "payments:manage",
      ],

      payments_claims: [
        "orders:read",
        "orders:manage",
        "payments:read",
        "payments:manage",
      ],

      submissions_review: [
        "submissions:read",
        "submissions:manage",
        "national_applications:read",
        "national_applications:review",
        "national_applications:approve",
        "national_applications:deny",
        "email_contacts:read",
        "email_contacts:manage",
      ],
    };

    const ministryScope = MINISTRY_DEFINITIONS.some((m) => m.key === scope);

    if (ministryScope) {
      return [
        "users:read",
        "users:manage",
        "roles:manage",
        "ministries:read",
        "ministries:manage",
      ];
    }

    return scopePermissions[scope] || [];
  };

  const toggleAdminScope = (scope) => {
  setFormData((prev) => {
    const currentScopes = prev.admin_scopes || [];
    const currentPermissions = prev.permissions || [];
    const currentRoles = prev.roles || [];

    const isRemoving = currentScopes.includes(scope);

    const nextScopes = isRemoving
      ? currentScopes.filter((s) => s !== scope)
      : [...currentScopes, scope];

    const scopeDefaultPermissions = getFrontendScopeDefaultPermissions(scope);

    let nextPermissions;

    if (isRemoving) {
      const permissionsStillNeededByOtherScopes = new Set(
        nextScopes.flatMap((s) => getFrontendScopeDefaultPermissions(s))
      );

      nextPermissions = currentPermissions.filter(
        (permission) =>
          !scopeDefaultPermissions.includes(permission) ||
          permissionsStillNeededByOtherScopes.has(permission)
      );
    } else {
      nextPermissions = Array.from(
        new Set([...currentPermissions, ...scopeDefaultPermissions])
      );
    }

    return {
      ...prev,
      roles: currentRoles.includes("admin")
        ? currentRoles
        : [...currentRoles, "admin"],
      admin_scopes: nextScopes,
      permissions: nextPermissions,
    };
  });
};
  
  const toggleAssetTier = (tier) => {
    const tiers = assetForm.access_tiers || [];

    setAssetForm({
      ...assetForm,
      access_tiers: tiers.includes(tier)
        ? tiers.filter((t) => t !== tier)
        : [...tiers, tier],
    });
  };

  const filteredUsers = users.filter((u) => {
    const search = searchTerm.toLowerCase();

    const matchesSearch =
      !searchTerm ||
      (u.username || "").toLowerCase().includes(search) ||
      (u.email || "").toLowerCase().includes(search) ||
      (u.display_name || "").toLowerCase().includes(search);

    const matchesRole = filterRole === "all" || (u.roles || []).includes(filterRole);

    return matchesSearch && matchesRole;
  });

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100" data-testid="nationals-admin-page">
      {/* Header */}
      <section className="bg-royal-blue text-white py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Link to="/portal" className="text-white/70 hover:text-white">
                <ArrowLeft className="w-5 h-5" />
              </Link>

              <div>
                <span className="text-kingdom-gold text-sm font-semibold uppercase tracking-wider">
                  Administration
                </span>
                <h1 className="font-heading text-2xl font-bold mt-1">
                  Admin Panel
                </h1>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {canViewDiagnostics && (
                <Link to="/admin/diagnostics">
                  <Button
                    className="bg-white/10 text-white hover:bg-white/20 font-semibold"
                    data-testid="admin-diagnostics-link"
                  >
                    <Shield className="w-4 h-4 mr-2" />
                    Diagnostics
                    {diagnosticUnread > 0 && <span aria-label={`${diagnosticUnread} new technical reports`} className="ml-2 rounded-full bg-red-600 px-1.5 text-xs text-white">{diagnosticUnread}</span>}
                  </Button>
                </Link>
              )}
              <Link to="/admin/submissions">
                <Button
                  className="bg-kingdom-gold text-royal-blue hover:bg-kingdom-gold/90 font-semibold"
                  data-testid="admin-submissions-link"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Submissions
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-6">
              <TabsTrigger value="email-directory">Email Directory</TabsTrigger>
              <TabsTrigger value="notifications"><span className="inline-flex items-center gap-2">Notifications <AdminNotificationCount /></span></TabsTrigger>
              <TabsTrigger value="users" data-testid="admin-tab-users">
                Users
              </TabsTrigger>
              <TabsTrigger value="assets" data-testid="admin-tab-assets">
                Subscriber Assets
              </TabsTrigger>
              <TabsTrigger value="products" data-testid="admin-tab-products">
                Products
              </TabsTrigger>
              {canViewDailyScripture && (
                <TabsTrigger value="scripture" data-testid="admin-tab-scripture">
                  Daily Scripture
                </TabsTrigger>
              )}
            </TabsList>

            {/* USERS TAB */}
            <TabsContent value="email-directory"><EmailDirectoryAdmin /></TabsContent>
            <TabsContent value="notifications"><AdminNotifications /></TabsContent>
            <TabsContent value="users">
              <div className="flex items-center gap-4 mb-6">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search users..."
                    className="pl-10"
                    data-testid="user-search-input"
                    style={{ fontSize: "16px" }}
                  />
                </div>

                <select
                  value={filterRole}
                  onChange={(e) => setFilterRole(e.target.value)}
                  className="px-3 py-2 border rounded text-sm"
                  data-testid="role-filter"
                >
                  <option value="all">All Roles</option>
                  <option value="national">YaSHaRUN</option>
                  <option value="subscriber">Subscriber</option>
                  <option value="admin">Admin</option>
                </select>

                {canManageUsers && (
                  <Button
                    className="btn-gold"
                    onClick={() => {
                      setFormData(blankUserForm);
                      setShowCreateModal(true);
                    }}
                    data-testid="create-user-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create User
                  </Button>
                )}
              </div>

              <Card>
                <CardContent className="p-0">
                  <div className="overflow-x-auto overscroll-x-contain">
                    <table className="w-full min-w-[850px] table-fixed" data-testid="users-table">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="p-3 text-left text-xs font-semibold text-slate-600">
                            User
                          </th>
                          <th className="p-3 text-left text-xs font-semibold text-slate-600">
                            Admin Level
                          </th>
                          <th className="p-3 text-left text-xs font-semibold text-slate-600">
                            Roles
                          </th>
                          <th className="p-3 text-left text-xs font-semibold text-slate-600">
                            Admin Scopes
                          </th>
                          <th className="p-3 text-left text-xs font-semibold text-slate-600">
                            Status
                          </th>
                          <th className="p-3 text-left text-xs font-semibold text-slate-600">
                            Ministries
                          </th>
                          <th className="p-3 text-left text-xs font-semibold text-slate-600">
                            Subscription
                          </th>
                          <th className="p-3 text-left text-xs font-semibold text-slate-600">
                            Actions
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {filteredUsers.map((u) => {
                          const uRoles = u.roles || [];
                          const uMins = normalizeMinistryKeys(u.ministries || []);
                          const uScopes = u.admin_scopes || [];
                          const adminLevel = getAdminLevel(u);

                          return (
                            <tr
                              key={u.id}
                              className="border-t hover:bg-slate-50"
                              data-testid={`user-row-${u.username}`}
                            >
                              <td className="p-3">
                                <div className="font-semibold text-sm">
                                  {u.display_name}
                                </div>
                                <div className="text-xs text-slate-500 break-all">
                                  @{u.username} | {u.email}
                                </div>
                              </td>

                              <td className="p-3">
                                <span className="px-2 py-1 bg-slate-100 text-slate-700 rounded text-xs">
                                  {adminLevel}
                                </span>
                              </td>

                              <td className="p-2 align-top">
                                {uRoles.slice(0, 2).map((r) => (
                                  <span
                                    key={r}
                                    className="px-1 py-0.5 bg-blue-100 text-blue-700 rounded text-xs mr-1 capitalize"
                                  >
                                    {r}
                                  </span>
                                ))}
                                {uRoles.length > 2 && <span className="text-xs text-slate-600" title={uRoles.slice(2).join(", ")}>+{uRoles.length - 2} roles</span>}
                              </td>

                              <td className="p-2 align-top w-[190px] max-w-[190px]">
                                {uScopes.length > 0 ? (
                                  <div className="flex flex-wrap gap-1 items-start">
                                    {uScopes.slice(0, 2).map((scope) => (
                                      <span key={scope}
                                        className="px-1 py-0.5 bg-amber-100 text-amber-800 rounded text-xs truncate max-w-[115px]"
                                        title={getAdminScopeLabel(scope) + ": " + getAdminScopeDescription(scope)}>
                                        {getAdminScopeLabel(scope)}
                                      </span>
                                    ))}
                                    {uScopes.length > 2 && (
                                      <details className="relative">
                                        <summary className="cursor-pointer text-xs font-semibold text-blue-800 rounded border px-1 py-0.5" aria-label={`Show ${uScopes.length - 2} more admin scopes`}>+${uScopes.length - 2} more</summary>
                                        <div className="absolute right-0 top-full z-20 max-h-52 overflow-auto w-64 max-w-[75vw] rounded border bg-white p-2 shadow-xl space-y-1">
                                          {uScopes.slice(2).map(scope => <p key={scope} className="text-xs break-words" title={getAdminScopeDescription(scope)}>{getAdminScopeLabel(scope)}</p>)}
                                        </div>
                                      </details>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-xs text-slate-400">-</span>
                                )}
                              </td>

                              <td className="p-3">
                                <span
                                  className={`px-2 py-1 rounded text-xs ${u.status === "active"
                                    ? "bg-green-100 text-green-700"
                                    : "bg-red-100 text-red-700"
                                    }`}
                                >
                                  {u.status}
                                </span>
                              </td>

                              <td className="p-3 max-w-[160px]">
                                {uMins.slice(0, 2).map((m) => (
                                  <span
                                    key={m}
                                    className="px-1 py-0.5 bg-purple-100 text-purple-700 rounded text-xs mr-1"
                                    title={getMinistryLabel(m)}
                                  >
                                    {getMinistryLabel(m).slice(0, 12)}
                                  </span>
                                ))}
                                {uMins.length > 2 && (
                                  <span className="text-xs text-slate-400">
                                    +{uMins.length - 2}
                                  </span>
                                )}
                              </td>

                              <td className="p-3">
                                {u.subscription_tier ? (
                                  <span className="px-1 py-0.5 bg-kingdom-gold/10 text-kingdom-gold rounded text-xs capitalize">
                                    {u.subscription_tier}
                                  </span>
                                ) : (
                                  <span className="text-xs text-slate-400">-</span>
                                )}
                              </td>

                              <td className="p-3">
                                <div className="flex gap-1">
                                  {canManageUsers && (
                                    <Button title="Edit user" aria-label="Edit user"
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => openEditModal(u)}
                                      data-testid={`edit-user-${u.username}`}
                                    >
                                      <Edit className="w-3 h-3" />
                                    </Button>
                                  )}

                                  {canManageUsers && (
                                    <Button title="Reset password" aria-label="Reset password"
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => {
                                        setSelectedUser(u);
                                        setResetPassword("");
                                        setShowResetModal(true);
                                      }}
                                    >
                                      <RotateCw className="w-3 h-3" />
                                    </Button>
                                  )}

                                  <Button title="View account audit" aria-label="View account audit"
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleViewAudit(u)}
                                  >
                                    <FileText className="w-3 h-3" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* DAILY SCRIPTURE TAB */}
            {canViewDailyScripture && (
              <TabsContent value="scripture">
                <DailyScriptureAdmin user={user} />
              </TabsContent>
            )}

            {/* ASSETS TAB */}
            <TabsContent value="assets">
              <div className="flex items-center gap-4 mb-6">
                <Label className="text-sm font-semibold">Month:</Label>

                <Input
                  type="month"
                  value={assetMonth}
                  onChange={(e) => setAssetMonth(e.target.value)}
                  className="w-48"
                  style={{ fontSize: "16px" }}
                  data-testid="asset-month-input"
                />

                {canManageAssets && (
                  <Button
                    className="btn-gold"
                    onClick={() => {
                      setEditingAsset(null);
                      setAssetForm({
                        title: "",
                        month_key: assetMonth,
                        asset_type: "download",
                        file_url: "",
                        thumbnail_url: "",
                        access_tiers: ["supporter", "builder", "patron"],
                        is_active: true,
                      });
                      setShowAssetModal(true);
                    }}
                    data-testid="create-asset-btn"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Publish Asset
                  </Button>
                )}
              </div>

              {assets.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Package className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-600">No assets for {assetMonth}</p>
                    <p className="text-sm text-slate-400 mt-1">
                      Publish an asset to make it available to subscribers.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-4">
                  {assets.map((a) => (
                    <Card
                      key={a.id}
                      className="card-official"
                      data-testid={`asset-card-${a.id}`}
                    >
                      <CardContent className="p-4 flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-royal-blue">
                              {a.title}
                            </h3>
                            <span
                              className={`px-2 py-0.5 rounded text-xs ${a.is_active
                                ? "bg-green-100 text-green-700"
                                : "bg-slate-100 text-slate-500"
                                }`}
                            >
                              {a.is_active ? "Active" : "Inactive"}
                            </span>
                            <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">
                              {a.asset_type}
                            </span>
                          </div>

                          <p className="text-xs text-slate-500">
                            Tiers: {(a.access_tiers || []).join(", ")} | URL:{" "}
                            <a
                              href={a.file_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 underline"
                            >
                              {a.file_url?.slice(0, 40)}...
                            </a>
                          </p>
                        </div>

                        {canManageAssets && (
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setEditingAsset(a);
                                setAssetForm({
                                  title: a.title,
                                  month_key: a.month_key,
                                  asset_type: a.asset_type,
                                  file_url: a.file_url,
                                  thumbnail_url: a.thumbnail_url || "",
                                  access_tiers: a.access_tiers || [],
                                  is_active: a.is_active,
                                });
                                setShowAssetModal(true);
                              }}
                            >
                              <Edit className="w-3 h-3" />
                            </Button>

                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-500"
                              onClick={() => handleDeleteAsset(a.id)}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* PRODUCTS TAB */}
            <TabsContent value="products">
              <div className="flex flex-wrap items-center gap-3 mb-6">
                {canManageProducts && (
                  <>
                    <Button
                      className="btn-gold"
                      onClick={openProductCreate}
                      data-testid="create-product-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      New Product
                    </Button>

                    <Button
                      variant="outline"
                      onClick={handleSeedMusic}
                      disabled={productSyncing}
                      data-testid="seed-music-btn"
                    >
                      <MusicIcon className="w-4 h-4 mr-2" />
                      Seed Music Products
                    </Button>

                    <Button
                      variant="outline"
                      onClick={handleEtsySync}
                      disabled={productSyncing}
                      data-testid="etsy-sync-btn"
                    >
                      <RefreshCw
                        className={`w-4 h-4 mr-2 ${productSyncing ? "animate-spin" : ""
                          }`}
                      />
                      Refresh Etsy Listings Cache
                    </Button>
                  </>
                )}
              </div>

              <Card className="mb-4 border-amber-300 bg-amber-50">
                <CardContent className="p-4 flex items-start gap-3">
                  <ShoppingBag className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-semibold text-amber-900">
                      Etsy listings shown here are a local cache / seeded records.
                    </p>
                    <p className="text-amber-800">
                      This is <strong>not</strong> a live Etsy Open API sync.
                      Refresh Etsy Listings Cache rewrites the known AncientPathFactory
                      listings into Mongo with outbound links to{" "}
                      <a
                        href="https://www.etsy.com/shop/AncientPathFactory"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline"
                      >
                        etsy.com/shop/AncientPathFactory
                      </a>
                      . Real-time inventory/pricing is on Etsy.
                    </p>
                  </div>
                </CardContent>
              </Card>

              {products.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Package className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-600">No products yet.</p>
                    <p className="text-sm text-slate-400 mt-1">
                      Use Seed Music Products or New Product.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <table className="w-full" data-testid="products-table">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="p-3 text-left text-xs font-semibold text-slate-600">
                              Title
                            </th>
                            <th className="p-3 text-left text-xs font-semibold text-slate-600">
                              Type
                            </th>
                            <th className="p-3 text-left text-xs font-semibold text-slate-600">
                              Source
                            </th>
                            <th className="p-3 text-left text-xs font-semibold text-slate-600">
                              Price
                            </th>
                            <th className="p-3 text-left text-xs font-semibold text-slate-600">
                              Active
                            </th>
                            <th className="p-3 text-left text-xs font-semibold text-slate-600">
                              Actions
                            </th>
                          </tr>
                        </thead>

                        <tbody>
                          {products.map((p) => (
                            <tr
                              key={p.id}
                              className="border-t hover:bg-slate-50"
                              data-testid={`product-row-${p.id}`}
                            >
                              <td className="p-3">
                                <div className="font-semibold text-sm">
                                  {p.title || p.name}
                                </div>
                                <div className="text-xs text-slate-500">
                                  {p.artist || p.description?.slice(0, 50)}
                                </div>
                              </td>

                              <td className="p-3">
                                <span className="px-1 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
                                  {p.product_type || p.category}
                                </span>
                              </td>

                              <td className="p-3">
                                <span
                                  className={`px-1 py-0.5 rounded text-xs ${p.source === "etsy"
                                    ? "bg-amber-100 text-amber-800"
                                    : "bg-purple-100 text-purple-700"
                                    }`}
                                >
                                  {p.source || "internal"}
                                </span>

                                {p.external_url && (
                                  <a
                                    href={p.external_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="ml-1 inline-flex"
                                  >
                                    <ExternalLink className="w-3 h-3 text-slate-400" />
                                  </a>
                                )}
                              </td>

                              <td className="p-3 text-sm font-semibold">
                                ${p.price?.toFixed?.(2) ?? p.price}
                              </td>

                              <td className="p-3">
                                <span
                                  className={`px-2 py-0.5 rounded text-xs ${p.is_active !== false
                                    ? "bg-green-100 text-green-700"
                                    : "bg-slate-100 text-slate-500"
                                    }`}
                                >
                                  {p.is_active !== false ? "Active" : "Inactive"}
                                </span>
                              </td>

                              <td className="p-3">
                                <div className="flex gap-1">
                                  {canManageProducts && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => openProductEdit(p)}
                                      data-testid={`edit-product-${p.id}`}
                                    >
                                      <Edit className="w-3 h-3" />
                                    </Button>
                                  )}

                                  {canManageProducts && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="text-red-500"
                                      onClick={() => handleDeleteProduct(p.id)}
                                      data-testid={`delete-product-${p.id}`}
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* CREATE USER MODAL */}
      {showCreateModal && (
        <UserModal
          title="Create User"
          formData={formData}
          setFormData={setFormData}
          applyPermissionPreset={applyPermissionPreset}
          toggleRole={toggleRole}
          toggleMinistry={toggleMinistry}
          toggleAdminScope={toggleAdminScope}
          togglePermission={togglePermission}
          onCancel={() => setShowCreateModal(false)}
          onSubmit={handleCreateUser}
          submitLabel="Create"
          submitTestId="submit-create-user"
          showLoginFields={true}
        />
      )}

      {/* EDIT USER MODAL */}
      {showEditModal && selectedUser && (
        <UserModal
          title={`Edit: ${selectedUser.display_name}`}
          subtitle={`@${selectedUser.username}`}
          formData={formData}
          applyPermissionPreset={applyPermissionPreset}
          setFormData={setFormData}
          toggleRole={toggleRole}
          toggleMinistry={toggleMinistry}
          toggleAdminScope={toggleAdminScope}
          togglePermission={togglePermission}
          onCancel={() => setShowEditModal(false)}
          onSubmit={handleUpdateUser}
          submitLabel="Save"
          submitTestId="submit-edit-user"
          showLoginFields={false}
        />
      )}

      {/* RESET PASSWORD MODAL */}
      {showResetModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Reset Password: {selectedUser.display_name}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>New Password</Label>
                <div className="relative">
                  <Input
                    type={showResetPassword ? "text" : "password"}
                    value={resetPassword}
                    onChange={(e) => setResetPassword(e.target.value)}
                    style={{ fontSize: "16px" }}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowResetPassword(!showResetPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showResetPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowResetModal(false)}
                >
                  Cancel
                </Button>
                <Button className="flex-1 btn-gold" onClick={handleResetPassword}>
                  Reset
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* AUDIT LOG MODAL */}
      {showAuditModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg">
            <CardHeader>
              <CardTitle>Audit: {selectedUser.display_name}</CardTitle>
            </CardHeader>

            <CardContent className="max-h-80 overflow-y-auto">
              {auditLogs.length === 0 ? (
                <p className="text-slate-500 text-sm">No audit entries</p>
              ) : (
                <div className="space-y-3">
                  {auditLogs.map((log, i) => (
                    <div key={i} className="text-xs border-b pb-3">
                      <div className="font-semibold text-royal-blue">
                        {log.action}
                      </div>

                      <div className="text-slate-600 mt-1">
                        Changed by:{" "}
                        <span className="font-semibold">
                          {log.actor_display_name ||
                            log.actor_username ||
                            log.actor_email ||
                            log.actor_user_id ||
                            "Unknown admin"}
                        </span>
                      </div>

                      {log.from_value && (
                        <div className="text-slate-500 mt-1">
                          From: {log.from_value}
                        </div>
                      )}

                      {log.to_value && (
                        <div className="text-slate-500 mt-1">
                          To: {log.to_value}
                        </div>
                      )}

                      {log.ip && (
                        <div className="text-slate-400 mt-1">
                          IP: {log.ip}
                        </div>
                      )}

                      <div className="text-slate-400 mt-1">
                        Date/Time: {new Date(log.created_at).toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <Button
                variant="outline"
                className="w-full mt-4"
                onClick={() => setShowAuditModal(false)}
              >
                Close
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ASSET CREATE/EDIT MODAL */}
      {
        showAssetModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-lg">
              <CardHeader>
                <CardTitle className="text-royal-blue">
                  {editingAsset ? "Edit Asset" : "Publish Asset"}
                </CardTitle>
              </CardHeader>

              <CardContent className="space-y-4">
                <div>
                  <Label className="text-xs">Title *</Label>
                  <Input
                    value={assetForm.title}
                    onChange={(e) =>
                      setAssetForm({ ...assetForm, title: e.target.value })
                    }
                    style={{ fontSize: "16px" }}
                    data-testid="asset-title-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs">Month</Label>
                    <Input
                      type="month"
                      value={assetForm.month_key}
                      onChange={(e) =>
                        setAssetForm({ ...assetForm, month_key: e.target.value })
                      }
                      style={{ fontSize: "16px" }}
                    />
                  </div>

                  <div>
                    <Label className="text-xs">Type</Label>
                    <select
                      value={assetForm.asset_type}
                      onChange={(e) =>
                        setAssetForm({ ...assetForm, asset_type: e.target.value })
                      }
                      className="w-full px-2 py-1 border rounded text-sm"
                    >
                      <option value="download">Download</option>
                      <option value="audio">Audio</option>
                      <option value="video">Video</option>
                      <option value="document">Document</option>
                    </select>
                  </div>
                </div>

                <div>
                  <Label className="text-xs">File URL *</Label>
                  <Input
                    value={assetForm.file_url}
                    onChange={(e) =>
                      setAssetForm({ ...assetForm, file_url: e.target.value })
                    }
                    placeholder="https://..."
                    style={{ fontSize: "16px" }}
                    data-testid="asset-url-input"
                  />
                </div>

                <div>
                  <Label className="text-xs">Thumbnail URL optional</Label>
                  <Input
                    value={assetForm.thumbnail_url}
                    onChange={(e) =>
                      setAssetForm({ ...assetForm, thumbnail_url: e.target.value })
                    }
                    style={{ fontSize: "16px" }}
                  />
                </div>

                <div>
                  <Label className="text-xs font-semibold">Access Tiers</Label>
                  <div className="flex gap-4 mt-1">
                    {["supporter", "builder", "patron"].map((t) => (
                      <label key={t} className="flex items-center gap-1 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={(assetForm.access_tiers || []).includes(t)}
                          onChange={() => toggleAssetTier(t)}
                        />
                        <span className="text-sm capitalize">{t}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={assetForm.is_active}
                    onChange={(e) =>
                      setAssetForm({ ...assetForm, is_active: e.target.checked })
                    }
                  />
                  <span className="text-sm">Active visible to subscribers</span>
                </label>

                <div className="flex gap-2 pt-2">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      setShowAssetModal(false);
                      setEditingAsset(null);
                    }}
                  >
                    Cancel
                  </Button>

                  <Button
                    className="flex-1 btn-gold"
                    onClick={editingAsset ? handleUpdateAsset : handleCreateAsset}
                    data-testid="submit-asset-btn"
                  >
                    {editingAsset ? "Save" : "Publish"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )
      }

      {/* PRODUCT MODAL */}
      {
        showProductModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <Card className="w-full max-w-lg my-8">
              <CardHeader>
                <CardTitle className="text-royal-blue">
                  {editingProduct ? "Edit Product" : "New Product"}
                </CardTitle>
                <CardDescription>
                  {editingProduct
                    ? `Editing ${editingProduct.id}`
                    : "Internal product or Etsy listing record"}
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-3 max-h-[70vh] overflow-y-auto">
                {!editingProduct && (
                  <div>
                    <Label className="text-xs">ID slug lowercase *</Label>
                    <Input
                      value={productForm.id}
                      onChange={(e) =>
                        setProductForm({ ...productForm, id: e.target.value })
                      }
                      data-testid="product-id-input"
                      style={{ fontSize: "16px" }}
                    />
                  </div>
                )}

                <div>
                  <Label className="text-xs">Title *</Label>
                  <Input
                    value={productForm.title}
                    onChange={(e) =>
                      setProductForm({ ...productForm, title: e.target.value })
                    }
                    data-testid="product-title-input"
                    style={{ fontSize: "16px" }}
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Category</Label>
                    <select
                      value={productForm.category}
                      onChange={(e) =>
                        setProductForm({ ...productForm, category: e.target.value })
                      }
                      className="w-full px-3 py-2 border rounded text-sm"
                    >
                      <option value="music">music</option>
                      <option value="merch">merch</option>
                      <option value="support">support</option>
                    </select>
                  </div>

                  <div>
                    <Label className="text-xs">Type</Label>
                    <select
                      value={productForm.product_type}
                      onChange={(e) =>
                        setProductForm({
                          ...productForm,
                          product_type: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border rounded text-sm"
                    >
                      <option value="music">music</option>
                      <option value="etsy_listing">etsy_listing</option>
                      <option value="donation">donation</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Price *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={productForm.price}
                      onChange={(e) =>
                        setProductForm({
                          ...productForm,
                          price: parseFloat(e.target.value) || 0,
                        })
                      }
                      data-testid="product-price-input"
                      style={{ fontSize: "16px" }}
                    />
                  </div>

                  <div>
                    <Label className="text-xs">Source</Label>
                    <select
                      value={productForm.source}
                      onChange={(e) =>
                        setProductForm({ ...productForm, source: e.target.value })
                      }
                      className="w-full px-3 py-2 border rounded text-sm"
                    >
                      <option value="internal">internal</option>
                      <option value="etsy">etsy cached</option>
                    </select>
                  </div>
                </div>

                <div>
                  <Label className="text-xs">Artist</Label>
                  <Input
                    value={productForm.artist}
                    onChange={(e) =>
                      setProductForm({ ...productForm, artist: e.target.value })
                    }
                    style={{ fontSize: "16px" }}
                  />
                </div>

                <div>
                  <Label className="text-xs">Description</Label>
                  <Input
                    value={productForm.description}
                    onChange={(e) =>
                      setProductForm({
                        ...productForm,
                        description: e.target.value,
                      })
                    }
                    style={{ fontSize: "16px" }}
                  />
                </div>

                <div>
                  <Label className="text-xs">Cover Image URL</Label>
                  <Input
                    value={productForm.cover_image_url}
                    onChange={(e) =>
                      setProductForm({
                        ...productForm,
                        cover_image_url: e.target.value,
                      })
                    }
                    style={{ fontSize: "16px" }}
                  />
                </div>

                <div>
                  <Label className="text-xs">Audio File URL</Label>
                  <Input
                    value={productForm.audio_file_url}
                    onChange={(e) =>
                      setProductForm({
                        ...productForm,
                        audio_file_url: e.target.value,
                      })
                    }
                    style={{ fontSize: "16px" }}
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Preview URL</Label>
                    <Input
                      value={productForm.preview_file_url}
                      onChange={(e) =>
                        setProductForm({
                          ...productForm,
                          preview_file_url: e.target.value,
                        })
                      }
                      style={{ fontSize: "16px" }}
                    />
                  </div>

                  <div>
                    <Label className="text-xs">Preview Sec</Label>
                    <Input
                      type="number"
                      value={productForm.preview_duration_seconds}
                      onChange={(e) =>
                        setProductForm({
                          ...productForm,
                          preview_duration_seconds:
                            parseInt(e.target.value, 10) || 20,
                        })
                      }
                      style={{ fontSize: "16px" }}
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-xs">External URL Etsy</Label>
                  <Input
                    value={productForm.external_url}
                    onChange={(e) =>
                      setProductForm({
                        ...productForm,
                        external_url: e.target.value,
                      })
                    }
                    style={{ fontSize: "16px" }}
                  />
                </div>

                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={productForm.digital}
                      onChange={(e) =>
                        setProductForm({ ...productForm, digital: e.target.checked })
                      }
                    />
                    <span className="text-sm">Digital</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={productForm.is_active}
                      onChange={(e) =>
                        setProductForm({
                          ...productForm,
                          is_active: e.target.checked,
                        })
                      }
                    />
                    <span className="text-sm">Active</span>
                  </label>
                </div>

                <div className="flex gap-2 pt-2">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      setShowProductModal(false);
                      setEditingProduct(null);
                    }}
                  >
                    Cancel
                  </Button>

                  <Button
                    className="flex-1 btn-gold"
                    onClick={handleSaveProduct}
                    data-testid="submit-product-btn"
                  >
                    {editingProduct ? "Save" : "Create"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )
      }
    </div >
  );
}

function UserModal({
  title,
  subtitle,
  formData,
  setFormData,
  applyPermissionPreset,
  toggleRole,
  togglePermission,
  toggleMinistry,
  toggleAdminScope,
  onCancel,
  onSubmit,
  submitLabel,
  submitTestId,
  showLoginFields,
}) {
  const selectedMinistries = normalizeMinistryKeys(formData.ministries || []);
  const selectedAdminScopes = formData.admin_scopes || [];
  const [showPassword, setShowPassword] = useState(false);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <Card className="w-full max-w-3xl my-8">
        <CardHeader>
          <CardTitle className="text-royal-blue">{title}</CardTitle>
          {subtitle && <CardDescription>{subtitle}</CardDescription>}
        </CardHeader>

        <CardContent className="space-y-4 max-h-[75vh] overflow-y-auto">
          {showLoginFields && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Email *</Label>
                  <Input
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    style={{ fontSize: "16px" }}
                    data-testid="create-email-input"
                  />
                </div>

                <div>
                  <Label className="text-xs">Username *</Label>
                  <Input
                    value={formData.username}
                    onChange={(e) =>
                      setFormData({ ...formData, username: e.target.value })
                    }
                    style={{ fontSize: "16px" }}
                    data-testid="create-username-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Display Name *</Label>
                  <Input
                    value={formData.display_name}
                    onChange={(e) =>
                      setFormData({ ...formData, display_name: e.target.value })
                    }
                    style={{ fontSize: "16px" }}
                  />
                </div>

                <div>
                  <Label className="text-xs">Password *</Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={(e) =>
                        setFormData({ ...formData, password: e.target.value })
                      }
                      style={{ fontSize: "16px" }}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}

          {!showLoginFields && (
            <div>
              <Label className="text-xs">Status</Label>
              <select
                value={formData.status}
                onChange={(e) =>
                  setFormData({ ...formData, status: e.target.value })
                }
                className="w-full px-2 py-1 border rounded text-sm"
              >
                <option value="active">Active</option>
                <option value="disabled">Disabled</option>
              </select>
            </div>
          )}

          <div>
            <Label className="text-xs font-semibold">Roles</Label>
            <div className="flex gap-4 mt-1">
              {["national", "subscriber", "yasharal_live", "records_label", "publishing", "music", "admin"].map((r) => (
                <label key={r} className="flex items-center gap-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={(formData.roles || []).includes(r)}
                    onChange={() => toggleRole(r)}
                  />
                  <span className="text-sm">{({national:"YaSHaRUN",subscriber:"Subscriber",yasharal_live:"Yashar’al Live",records_label:"Records Label",publishing:"Publishing",music:"Music",admin:"Admin"})[r]}</span>
                </label>
              ))}
            </div>
          </div>

          {(formData.roles || []).includes("admin") && (
            <section className="rounded-lg border border-blue-200 bg-blue-50 p-3 space-y-3" data-testid="admin-business-capabilities">
              <h3 className="font-semibold text-sm text-blue-950">Administration Areas</h3>
              <p className="text-xs text-blue-900">Assign operational responsibilities independently of YaSHaRUN or Subscriber membership. These controls grant administrator permissions, not paid service access.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {EXTENDED_ADMIN_SCOPES.map(area => (
                  <label key={area.key} className="flex items-start gap-2 text-sm">
                    <input type="checkbox" className="mt-1"
                      checked={(formData.admin_scopes || []).includes(area.key)}
                      onChange={() => toggleAdminScope(area.key)} />
                    <span><strong>{area.label}</strong><span className="block text-xs text-slate-600">{area.description}</span></span>
                  </label>
                ))}
              </div>
            </section>
          )}

          {(formData.roles || []).includes("admin") && (
            <div>
              <Label className="text-xs font-semibold">Permission Presets</Label>

              <p className="text-xs text-gray-500 mt-1 mb-3">
                Presets add common access. They do not remove existing access. Admin Scopes show what area this admin may manage.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2 mt-2 mb-4">
                <Button type="button" variant="outline" size="sm" onClick={() => applyPermissionPreset("mini_super_admin")}>
                  Mini Super Admin
                </Button>

                <Button type="button" variant="outline" size="sm" onClick={() => applyPermissionPreset("portal_admin")}>
                  Website / Portal Admin
                </Button>

                <Button type="button" variant="outline" size="sm" onClick={() => applyPermissionPreset("assets_admin")}>
                  Subscriber Assets Admin
                </Button>

                <Button type="button" variant="outline" size="sm" onClick={() => applyPermissionPreset("products_admin")}>
                  Store Products Admin
                </Button>

                <Button type="button" variant="outline" size="sm" onClick={() => applyPermissionPreset("subscriptions_admin")}>
                  Subscription Admin
                </Button>

                <Button type="button" variant="outline" size="sm" onClick={() => applyPermissionPreset("submissions_admin")}>
                  Submissions Admin
                </Button>

                <Button type="button" variant="outline" size="sm" onClick={() => applyPermissionPreset("ministry_admin")}>
                  Ministry User Admin
                </Button>

                <Button type="button" variant="outline" size="sm" onClick={() => applyPermissionPreset("user_admin")}>
                  User Management Admin
                </Button>
              </div>

              <Label className="text-xs font-semibold">Permissions</Label>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-1">
                {ALL_PERMISSIONS.map((p) => (
                  <label
                    key={p.key}
                    className="flex items-start gap-2 cursor-pointer text-xs"
                  >
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={(formData.permissions || []).includes(p.key)}
                      onChange={() => togglePermission(p.key)}
                    />

                    <span>
                      <span className="font-semibold text-slate-700 inline-flex items-center gap-1">
                        {p.label}
                        <Info
                          className="w-3 h-3 text-slate-400"
                          title={p.description}
                        />
                      </span>

                      <span className="block text-slate-500">
                        {p.description}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {(formData.roles || []).includes("admin") && (
            <div>
              <Label className="text-xs font-semibold">Admin Scopes</Label>

              <div className="space-y-3 mt-2 border rounded p-3 bg-slate-50">
                <label className="flex items-start gap-2 cursor-pointer text-xs">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={selectedAdminScopes.includes("all_except_super_admin")}
                    onChange={() => toggleAdminScope("all_except_super_admin")}
                  />

                  <span>
                    <span className="font-semibold text-slate-700">
                      Mini Super Admin
                    </span>

                    <span className="block text-slate-500">
                      Can manage normal users and admin areas, but cannot change the protected super admin account.
                    </span>
                  </span>
                </label>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 max-h-72 overflow-y-auto">
                  {ALL_ADMIN_SCOPES.map((scope) => (
                    <label
                      key={scope.key}
                      className="flex items-start gap-2 cursor-pointer text-xs"
                      title={scope.description}
                    >
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={selectedAdminScopes.includes(scope.key)}
                        onChange={() => toggleAdminScope(scope.key)}
                      />

                      <span>
                        <span className="font-semibold text-slate-700">
                          Manage {scope.label}
                        </span>

                        <span className="block text-slate-500">
                          {scope.description}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}
          {(formData.roles || []).includes("subscriber") && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Subscription Tier</Label>
                <select
                  value={formData.subscription_tier || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, subscription_tier: e.target.value })
                  }
                  className="w-full px-2 py-1 border rounded text-sm"
                >
                  <option value="">None</option>
                  <option value="supporter">Supporter</option>
                  <option value="builder">Builder</option>
                  <option value="patron">Patron</option>
                </select>
              </div>

              <div>
                <Label className="text-xs">Subscription Status</Label>
                <select
                  value={formData.subscription_status || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, subscription_status: e.target.value })
                  }
                  className="w-full px-2 py-1 border rounded text-sm"
                >
                  <option value="">None</option>
                  <option value="active">Active</option>
                  <option value="expired">Expired</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>
          )}

          {(formData.roles || []).includes("national") && (
            <div>
              <Label className="text-xs font-semibold">Ministries</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 mt-1 max-h-80 overflow-y-auto border rounded p-3 bg-slate-50">
                {MINISTRY_DEFINITIONS.map((ministry) => (
                  <label
                    key={ministry.key}
                    className="flex items-start gap-2 cursor-pointer text-xs"
                  >
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={selectedMinistries.includes(ministry.key)}
                      onChange={() => toggleMinistry(ministry.key)}
                    />
                    <span>
                      <span className="font-semibold text-slate-700">
                        {ministry.label}
                      </span>
                      <span className="block text-slate-500">
                        {ministry.description}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-2 pt-4">
            <Button variant="outline" className="flex-1" onClick={onCancel}>
              Cancel
            </Button>

            <Button
              className="flex-1 btn-gold"
              onClick={onSubmit}
              data-testid={submitTestId}
            >
              {submitLabel}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}        
